import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { auth as authApi } from '@/api/endpoints';
import { Notice } from '@/components/Notice';
import { describe } from '@/lib/errors';
import { formatDateTime } from '@/lib/format';
import { useAuth } from '@/stores/auth';

export function SettingsPage() {
  const user = useAuth((s) => s.user);
  const queryClient = useQueryClient();

  const [enrolment, setEnrolment] = useState<{ secret: string; uri: string } | null>(null);
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [message, setMessage] = useState<{ tone: 'info' | 'error'; text: string } | null>(null);

  async function startEnrolment() {
    try {
      const result = await authApi.enrollMfa();
      setEnrolment({ secret: result.secret, uri: result.provisioning_uri });
      setMessage(null);
    } catch (err) {
      setMessage({ tone: 'error', text: describe(err) });
    }
  }

  async function confirmEnrolment() {
    try {
      const result = await authApi.confirmMfa(code);
      setRecoveryCodes(result.recovery_codes);
      setEnrolment(null);
      setCode('');
      void queryClient.invalidateQueries();
      setMessage({ tone: 'info', text: 'Two-factor authentication is on for this account.' });
    } catch (err) {
      setMessage({ tone: 'error', text: describe(err) });
    }
  }

  async function changePassword(event: React.FormEvent) {
    event.preventDefault();
    try {
      const result = await authApi.changePassword(currentPassword, newPassword);
      setMessage({
        tone: 'info',
        text: `Password changed. ${result.sessions_revoked} other ${
          result.sessions_revoked === 1 ? 'session was' : 'sessions were'
        } signed out.`,
      });
      setCurrentPassword('');
      setNewPassword('');
    } catch (err) {
      setMessage({ tone: 'error', text: describe(err) });
    }
  }

  if (!user) return null;

  return (
    <>
      <div className="page-head">
        <h1>Your account</h1>
        <p>
          {user.email} · {user.role.replace('_', ' ').toLowerCase()} · last signed in{' '}
          {formatDateTime(user.last_login_at)}
        </p>
      </div>

      {message && <Notice tone={message.tone}>{message.text}</Notice>}

      <div className="grid-2">
        <section className="panel">
          <div className="panel-head">
            <h2>Two-factor authentication</h2>
          </div>
          <div className="panel-body" style={{ display: 'grid', gap: 'var(--space-4)' }}>
            {user.mfa_enabled && !recoveryCodes && <p>On. Your authenticator is required to sign in.</p>}

            {recoveryCodes && (
              <div>
                <p>
                  Save these recovery codes somewhere safe. Each works once, and they are shown only
                  now.
                </p>
                <ul className="num" style={{ paddingLeft: '1.2em' }}>
                  {recoveryCodes.map((codeValue) => (
                    <li key={codeValue}>{codeValue}</li>
                  ))}
                </ul>
              </div>
            )}

            {!user.mfa_enabled && !enrolment && (
              <>
                <p>
                  Off. Accounts that can change credentials or risk limits should have it on.
                </p>
                <button type="button" className="primary" onClick={startEnrolment}>
                  Set up two-factor
                </button>
              </>
            )}

            {enrolment && (
              <>
                <p>Add this key to your authenticator app, then enter the six-digit code it shows.</p>
                <code className="num" style={{ wordBreak: 'break-all' }}>
                  {enrolment.secret}
                </code>
                <div>
                  <label htmlFor="mfa-code">Code</label>
                  <input
                    id="mfa-code"
                    inputMode="numeric"
                    value={code}
                    onChange={(event) => setCode(event.target.value)}
                  />
                </div>
                <button
                  type="button"
                  className="primary"
                  onClick={confirmEnrolment}
                  disabled={code.length < 6}
                >
                  Turn on two-factor
                </button>
              </>
            )}
          </div>
        </section>

        <section className="panel">
          <div className="panel-head">
            <h2>Password</h2>
          </div>
          <div className="panel-body">
            <form onSubmit={changePassword} style={{ display: 'grid', gap: 'var(--space-4)' }}>
              <div>
                <label htmlFor="current-password">Current password</label>
                <input
                  id="current-password"
                  type="password"
                  autoComplete="current-password"
                  value={currentPassword}
                  onChange={(event) => setCurrentPassword(event.target.value)}
                />
              </div>
              <div>
                <label htmlFor="new-password">New password</label>
                <input
                  id="new-password"
                  type="password"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                />
                <p className="hint" style={{ marginTop: 'var(--space-2)' }}>
                  At least 14 characters, using three of: lowercase, uppercase, digits, symbols.
                  Changing it signs out your other sessions.
                </p>
              </div>
              <button
                type="submit"
                className="primary"
                disabled={!currentPassword || !newPassword}
                style={{ justifySelf: 'start' }}
              >
                Change password
              </button>
            </form>
          </div>
        </section>
      </div>

      <section className="panel">
        <div className="panel-head">
          <h2>What you can do</h2>
        </div>
        <div className="panel-body">
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>
            Your role grants these permissions. The server enforces them; the interface only hides
            what it would refuse.
          </p>
          <ul className="num" style={{ paddingLeft: '1.2em', fontSize: 'var(--text-sm)' }}>
            {user.permissions.map((permission) => (
              <li key={permission}>{permission}</li>
            ))}
          </ul>
        </div>
      </section>
    </>
  );
}
